import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useContext } from 'react';
import { AuthContext } from 'components/authentication/context/authContext';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import ReactIntlWrapper from 'components/i18n/ReactIntlWrapper';
import { getApiKey } from 'components/authentication/context/authContext';
import globalAxios from 'axios';

export interface BulkImportRowRequest {
  date: string;
  amount: number;  // positive = credit, negative = debit
  description: string;
  dateFormat: string;
}

export interface BulkImportRequest {
  idAccount: number;
  rows: BulkImportRowRequest[];
}

export interface BulkImportResultResponse {
  imported: number;
  failed: number;
  errors: string[];
}

/**
 * Movement creation/update request data
 */
export interface MovementRequest {
  date: string;
  amount: number;
  incoming: number; // 1 = credit, 0 = debit
  description: string;
  idAccount: number;
  idCurrency: number;
  movementType?: string | null;       // null = regular, 'transfer' = transfer
  idRelatedAccount?: number | null;   // required when movementType === 'transfer'
  exchangeRate?: number | null;       // required when accounts have different currencies
}

/**
 * Hook for movement CRUD operations
 */
export const useMovementOperations = () => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastMessageQueue();

  // Create movement mutation
  const createMovement = useMutation({
    mutationFn: async (movementData: MovementRequest) => {
      const response = await invoiceApi.apiVversionLedgerPost('1', {
        ...movementData,
        amount: Math.round(movementData.amount * 100),
        date: new Date(movementData.date),
        userEntityId: userEntityDetails?.id,
        movementType: movementData.movementType ?? null,
        idRelatedAccount: movementData.idRelatedAccount ?? null,
        exchangeRate: movementData.exchangeRate ?? null,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate ledger movements cache for the specific account
      queryClient.invalidateQueries({ 
        queryKey: ['ledgerMovements', variables.idAccount] 
      });
      
      // Also invalidate account balance cache
      queryClient.invalidateQueries({ 
        queryKey: ['accountBalance', variables.idAccount] 
      });

      // Show success notification
      success({
        message: ReactIntlWrapper.get('successcodes.movementCreatedSuccessfully')
      });
    },
    onError: (error: any) => {
      console.error('Error creating movement:', error);
      const labelKey = error?.response?.data?.errors?.[0]?.errorLabelKey;
      showError({
        message: labelKey
          ? ReactIntlWrapper.get(labelKey)
          : ReactIntlWrapper.get('errorcodes.errorCreatingMovement'),
      });
    },
  });

  // Update movement mutation
  const updateMovement = useMutation({
    mutationFn: async ({ id, movementData }: { id: number; movementData: MovementRequest }) => {
      const response = await invoiceApi.apiVversionLedgerIdPut(id, '1', {
        ...movementData,
        id,
        amount: Math.round(movementData.amount * 100),
        incoming: movementData.incoming,
        date: new Date(movementData.date),
        userEntityId: userEntityDetails?.id,
        exchangeRate: movementData.exchangeRate ?? null,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate ledger movements cache for the specific account
      queryClient.invalidateQueries({ 
        queryKey: ['ledgerMovements', variables.movementData.idAccount] 
      });
      
      // Also invalidate account balance cache
      queryClient.invalidateQueries({ 
        queryKey: ['accountBalance', variables.movementData.idAccount] 
      });

      // Show success notification
      success({
        message: ReactIntlWrapper.get('successcodes.movementUpdatedSuccessfully')
      });
    },
    onError: (error) => {
      console.error('Error updating movement:', error);
      // Show error notification
      showError({
        message: ReactIntlWrapper.get('errorcodes.errorUpdatingMovement')
      });
    },
  });

  // Delete movement mutation (optional - not in requirements but useful)
  const deleteMovement = useMutation({
    mutationFn: async ({ id, accountId }: { id: number; accountId: number }) => {
      const response = await invoiceApi.apiVversionLedgerIdDelete(id, '1');
      return { response: response.data, accountId };
    },
    onSuccess: (data) => {
      // Invalidate ledger movements cache for the specific account
      queryClient.invalidateQueries({ 
        queryKey: ['ledgerMovements', data.accountId] 
      });
      
      // Also invalidate account balance cache
      queryClient.invalidateQueries({ 
        queryKey: ['accountBalance', data.accountId] 
      });

      // Show success notification
      success({
        message: ReactIntlWrapper.get('successcodes.movementDeletedSuccessfully')
      });
    },
    onError: (error) => {
      console.error('Error deleting movement:', error);
      // Show error notification
      showError({
        message: ReactIntlWrapper.get('errorcodes.errorDeletingMovement')
      });
    },
  });

  // Bulk import movements from CSV data
  const bulkImportMovements = useMutation({
    mutationFn: async (importData: BulkImportRequest): Promise<BulkImportResultResponse> => {
      const token = await getApiKey('Authorization');
      const userEntityHeader = userEntityDetails?.id?.toString() ?? '';
      // Access basePath from the underlying InvoiceApi instance
      const basePath: string = (invoiceApi as any).basePath ?? '';
      const response = await globalAxios.post<{ data: BulkImportResultResponse }>(
        `${basePath}/api/v1/Ledger/bulk-import`,
        {
          idAccount: importData.idAccount,
          rows: importData.rows,
          userEntityId: userEntityDetails?.id,
        },
        {
          headers: {
            Authorization: token,
            userentity: userEntityHeader,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ledgerMovements', variables.idAccount] });
      queryClient.invalidateQueries({ queryKey: ['accountBalance', variables.idAccount] });
    },
    onError: (error: any) => {
      console.error('Error bulk importing movements:', error);
    },
  });

  return {
    createMovement,
    updateMovement,
    deleteMovement,
    bulkImportMovements,
    // Helper function to prepare movement data
    prepareMovementData: (formData: any, accountId: number, currencyId: number = 1): MovementRequest => {
      return {
        date: formData.date,
        amount: formData.amount,
        incoming: formData.incoming,
        description: formData.description,
        idAccount: accountId,
        idCurrency: currencyId,
        movementType: formData.movementType ?? null,
        idRelatedAccount: formData.idRelatedAccount ?? null,
        exchangeRate: formData.exchangeRate ?? null,
      };
    },
  };
};

export default useMovementOperations;